import { DomainAdapterError } from "@validereinc/domain";
import {
  AuthenticationError,
  AuthorizationError,
  BaseError,
  DuplicatePropertyError,
  ExportError,
  HTTPRequestError,
  HTTPResponseError,
  InputError,
  InvariantError,
  KeyValueStorageError,
  MutationError,
  NativeError,
  NonExistentPropertyError,
  SchemaValidationError,
  UnknownError,
  ValidationError,
  type CustomErrorInstanceType,
} from "@validereinc/utilities";
import { useEffect, useRef } from "react";

/**
 * A hook that stores the previous value of a variable. Useful for scenarios
 * like comparing previous props and current props in dependency arrays of
 * useEffect, useMemo, useCallback, etc.
 */
export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T | undefined>(undefined);

  // run every render
  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
};

/**
 * [Normalize](https://github.com/ehmicky/modern-errors?tab=readme-ov-file#-normalize-errors) an error to a custom error class instance.
 * @see {@link BaseError} see the '@validereinc/utilities' package for the list of error classes and how custom error classes are implemented.
 * @param err - The error to normalize.
 * @param wrapHTTPErrors - Whether to wrap HTTP errors in a DomainAdapterError.
 * @returns The normalized error.
 */
export const normalizeError = (
  err: unknown,
  { wrapHTTPErrors = true }: { wrapHTTPErrors?: boolean } = {}
): CustomErrorInstanceType<
  | typeof DomainAdapterError
  | typeof HTTPResponseError
  | typeof HTTPRequestError
  | typeof NativeError
  | typeof InputError
  | typeof ValidationError
  | typeof SchemaValidationError
  | typeof NonExistentPropertyError
  | typeof DuplicatePropertyError
  | typeof KeyValueStorageError
  | typeof MutationError
  | typeof ExportError
  | typeof BaseError
> => {
  if (err instanceof DomainAdapterError) {
    return DomainAdapterError.normalize(err);
  } else if (err instanceof HTTPResponseError) {
    return wrapHTTPErrors
      ? new DomainAdapterError("", { cause: err })
      : HTTPResponseError.normalize(err);
  } else if (err instanceof HTTPRequestError) {
    return wrapHTTPErrors
      ? new DomainAdapterError("", { cause: err })
      : HTTPRequestError.normalize(err);
  } else if (err instanceof NativeError) {
    return NativeError.normalize(err);
  } else if (err instanceof InputError) {
    return InputError.normalize(err);
  } else if (err instanceof ValidationError) {
    return ValidationError.normalize(err);
  } else if (err instanceof SchemaValidationError) {
    return SchemaValidationError.normalize(err);
  } else if (err instanceof InvariantError) {
    return InvariantError.normalize(err);
  } else if (err instanceof NonExistentPropertyError) {
    return NonExistentPropertyError.normalize(err);
  } else if (err instanceof DuplicatePropertyError) {
    return DuplicatePropertyError.normalize(err);
  } else if (err instanceof KeyValueStorageError) {
    return KeyValueStorageError.normalize(err);
  } else if (err instanceof MutationError) {
    return MutationError.normalize(err);
  } else if (err instanceof ExportError) {
    return ExportError.normalize(err);
  } else if (err instanceof AuthenticationError) {
    return AuthenticationError.normalize(err);
  } else if (err instanceof AuthorizationError) {
    return AuthorizationError.normalize(err);
  } else if (err instanceof Error) {
    return new UnknownError("", { cause: err });
  } else {
    return UnknownError.normalize(err);
  }
};
